/*
 * Scroll-driven effects, both run from one rAF-throttled loop:
 *   1. [data-promo-zoom]   — the media inside slowly zooms in as the panel
 *                            travels across the screen.
 *   2. [data-scroll-reveal] — the container's direct children fade up one
 *                            after another as it rises up the screen.
 *   3. [data-split-text]   — the element's text starts barely visible and
 *                            darkens letter by letter in reading order.
 *   4. [data-scroll-fade]  — the element fades in on its way into the
 *                            viewport and back out as it leaves.
 *
 * Nothing here hijacks scrolling — it only reads scroll position. If this
 * script never runs, every element stays fully visible.
 */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // More than one section may pull this file in on the same page; the
  // browser fetches it once but would still run it once per tag, doubling
  // the scroll listeners.
  if (window.__scrollEffectsReady) return;
  window.__scrollEffectsReady = true;

  // Reveal tuning. Starting only once the block is well up the screen reads
  // as the content appearing late; finishing too low means nobody sees the
  // movement at all. This window sits between the two.
  var START_AT = 0.84;
  var END_AT = 0.42;
  var STAGGER = 0.18;
  var SPAN = 0.5;
  var SHIFT = 16;

  // Letter sweep. Runs over a longer stretch than the block reveal so the
  // wave across a long heading stays readable rather than snapping.
  var SPLIT_START = 0.85;
  var SPLIT_END = 0.3;
  var SPLIT_SPAN = 0.25;
  var SPLIT_FAINT = 0.15;

  // Fade envelope, measured against how far the element has travelled across
  // the screen: in over the first stretch, held through the middle, out over
  // the last.
  var FADE_IN_END = 0.28;
  var FADE_OUT_START = 0.72;

  var zoomTargets = [];
  var revealTargets = [];
  var splitTargets = [];
  var fadeTargets = [];
  var ticking = false;

  function clamp(value) {
    if (value < 0) return 0;
    if (value > 1) return 1;
    return value;
  }

  // Wraps every character in its own span so each can be faded separately.
  // Only text nodes are touched, so any markup inside the heading (links,
  // <em>, line breaks) survives intact. Whitespace is left as real text, so
  // lines still break normally and copying the heading yields the original
  // string. Letters stay `inline` — making them inline-block would change
  // how the text is laid out.
  function splitLetters(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
    var textNodes = [];

    while (walker.nextNode()) {
      if (walker.currentNode.nodeValue.trim()) textNodes.push(walker.currentNode);
    }

    textNodes.forEach(function (node) {
      var fragment = document.createDocumentFragment();

      node.nodeValue.split(/(\s+)/).forEach(function (part) {
        if (!part) return;

        if (/^\s+$/.test(part)) {
          fragment.appendChild(document.createTextNode(part));
          return;
        }

        for (var i = 0; i < part.length; i++) {
          var letter = document.createElement('span');
          letter.className = 'split-letter';
          letter.textContent = part.charAt(i);
          fragment.appendChild(letter);
        }
      });

      node.parentNode.replaceChild(fragment, node);
    });

    el.setAttribute('data-split-done', '');
    return el.querySelectorAll('.split-letter');
  }

  function collect() {
    zoomTargets = [];
    revealTargets = [];
    splitTargets = [];
    fadeTargets = [];

    document.querySelectorAll('[data-scroll-fade]').forEach(function (el) {
      fadeTargets.push({ el: el, written: -1 });
    });

    document.querySelectorAll('[data-promo-zoom]').forEach(function (wrap) {
      var media = wrap.querySelector('.promo-vertical__image, .promo-vertical__video, .promo-cards__image');
      if (!media) return;

      var strength = parseFloat(wrap.dataset.promoZoom);
      if (isNaN(strength) || strength <= 0) return;

      zoomTargets.push({ el: wrap, media: media, strength: strength / 100 });
    });

    document.querySelectorAll('[data-scroll-reveal]').forEach(function (content) {
      var items = content.children;
      if (!items.length) return;

      revealTargets.push({ el: content, items: items });
    });

    document.querySelectorAll('[data-split-text]').forEach(function (el) {
      // Re-running collect() must not split an already-split heading again,
      // which would nest a span inside every existing letter.
      var letters = el.hasAttribute('data-split-done')
        ? el.querySelectorAll('.split-letter')
        : splitLetters(el);
      if (!letters.length) return;

      var written = [];
      for (var i = 0; i < letters.length; i++) written.push(-1);

      splitTargets.push({ el: el, letters: letters, written: written });
    });

  }

  function update() {
    ticking = false;
    var viewportHeight = window.innerHeight;

    zoomTargets.forEach(function (target) {
      var rect = target.el.getBoundingClientRect();
      // 0 the moment the panel's top edge reaches the bottom of the screen,
      // 1 once its bottom edge has cleared the top — so the zoom runs for
      // the whole time the panel is on screen rather than only while it is
      // centred.
      var progress = clamp((viewportHeight - rect.top) / (viewportHeight + rect.height));

      target.media.style.transform = 'scale(' + (1 + progress * target.strength).toFixed(4) + ')';
    });

    fadeTargets.forEach(function (target) {
      var rect = target.el.getBoundingClientRect();
      var travel = clamp((viewportHeight - rect.top) / (viewportHeight + rect.height));

      var value;
      if (travel < FADE_IN_END) {
        value = travel / FADE_IN_END;
      } else if (travel > FADE_OUT_START) {
        value = (1 - travel) / (1 - FADE_OUT_START);
      } else {
        value = 1;
      }
      value = clamp(value);

      if (Math.abs(value - target.written) < 0.004) return;

      target.written = value;
      target.el.style.opacity = value.toFixed(3);
    });

    // The letter sweep runs everywhere: it is not tied to the sticky column,
    // and it costs one opacity write per letter that actually changed.
    splitTargets.forEach(function (target) {
      var start = viewportHeight * SPLIT_START;
      var end = viewportHeight * SPLIT_END;
      var progress =
        start > end ? clamp((start - target.el.getBoundingClientRect().top) / (start - end)) : 1;

      var letters = target.letters;
      var count = letters.length;
      // Each letter's ramp is SPLIT_SPAN long; the rest of the progress is
      // spread across the line-up so they light up one after another.
      var lead = count > 1 ? (1 - SPLIT_SPAN) / (count - 1) : 0;

      for (var i = 0; i < count; i++) {
        var local = clamp((progress - i * lead) / SPLIT_SPAN);
        var value = local >= 1 ? 1 : SPLIT_FAINT + (1 - SPLIT_FAINT) * local;

        // Most letters sit at one end or the other on any given frame, so
        // skipping unchanged writes keeps a long heading cheap.
        if (Math.abs(value - target.written[i]) < 0.004) continue;

        target.written[i] = value;
        letters[i].style.opacity = value.toFixed(3);
      }
    });

    revealTargets.forEach(function (target) {
      var start = viewportHeight * START_AT;
      var end = viewportHeight * END_AT;
      var progress =
        start > end ? clamp((start - target.el.getBoundingClientRect().top) / (start - end)) : 1;

      for (var i = 0; i < target.items.length; i++) {
        var local = clamp((progress - i * STAGGER) / SPAN);
        var eased = 1 - Math.pow(1 - local, 3);

        target.items[i].style.opacity = eased.toFixed(3);
        target.items[i].style.transform = 'translateY(' + ((1 - eased) * SHIFT).toFixed(2) + 'px)';
      }
    });
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  collect();
  update();

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate);

  // Scrolling is not the only thing that moves these elements: a lazy image
  // loading further up the page, a webfont swapping in or an embed resizing
  // all shift them without firing a scroll event, which would otherwise
  // leave the effect frozen at a stale position.
  window.addEventListener('load', requestUpdate);

  if (window.ResizeObserver) {
    new ResizeObserver(requestUpdate).observe(document.body);
  }

  // Re-scan when the theme editor swaps a section out, otherwise the client
  // would be left looking at unanimated content until they reload.
  document.addEventListener('shopify:section:load', function () {
    collect();
    requestUpdate();
  });

})();
